import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type Theme = "dragons-hoard" | "talktok" | "dragons-hoard-light";

const THEMES: Theme[] = ["dragons-hoard", "talktok", "dragons-hoard-light"];

const STORAGE_KEY = "comodo.ui.theme";
const MIGRATION_KEY = "comodo.ui.theme.migrated-v2";

function isTheme(v: string | null): v is Theme {
  return THEMES.includes(v as Theme);
}

const ThemeContext = createContext<{ theme: Theme; setTheme: (t: Theme) => void }>({
  theme: "dragons-hoard-light",
  setTheme: () => {},
});

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    // One-time default correction: devices that picked up "talktok" before
    // dragons-hoard-light became the default get moved once; re-selecting
    // talktok afterwards sticks.
    if (stored === "talktok" && !localStorage.getItem(MIGRATION_KEY)) {
      localStorage.setItem(MIGRATION_KEY, "1");
      return "dragons-hoard-light";
    }
    return isTheme(stored) ? stored : "dragons-hoard-light";
  });

  useEffect(() => {
    const el = document.documentElement;
    if (theme === "dragons-hoard") {
      el.removeAttribute("data-theme");
    } else {
      el.setAttribute("data-theme", theme);
    }
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  return <ThemeContext.Provider value={{ theme, setTheme }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
